import {
  ArrowLeftIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Loading from '../components/ui/Loading';
import apiService from '../services/api';
import { Protocol, ProtocolCategory } from '../types';

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categoryInfo: Record<
    string,
    {
      label: string;
      icon: React.ReactNode;
      description: string;
      color: string;
    }
  > = {
    medical: {
      label: 'Protocolos Médicos',
      icon: <HeartIcon className='h-8 w-8 text-red-500' />,
      description: 'Protocolos para el manejo de condiciones médicas generales y especializadas',
      color: 'red',
    },
    surgical: {
      label: 'Protocolos Quirúrgicos',
      icon: <ShieldCheckIcon className='h-8 w-8 text-blue-500' />,
      description: 'Protocolos para procedimientos quirúrgicos y perioperatorios',
      color: 'blue',
    },
    cardiac: {
      label: 'Protocolos Cardíacos',
      icon: <HeartIcon className='h-8 w-8 text-red-600' />,
      description: 'Protocolos especializados en cardiología y cirugía cardiovascular',
      color: 'red',
    },
    emergency: {
      label: 'Protocolos de Emergencia',
      icon: <ExclamationTriangleIcon className='h-8 w-8 text-yellow-500' />,
      description: 'Protocolos para el manejo de emergencias médicas y urgencias',
      color: 'yellow',
    },
    pediatric: {
      label: 'Protocolos Pediátricos',
      icon: <UserGroupIcon className='h-8 w-8 text-green-500' />,
      description: 'Protocolos especializados para el cuidado de pacientes pediátricos',
      color: 'green',
    },
    geriatric: {
      label: 'Protocolos Geriátricos',
      icon: <ChartBarIcon className='h-8 w-8 text-purple-500' />,
      description: 'Protocolos para el manejo de condiciones en pacientes geriátricos',
      color: 'purple',
    },
  };

  const currentCategory = categoryInfo[category || ''] || {
    label: 'Categoría no encontrada',
    icon: <DocumentTextIcon className='h-8 w-8 text-gray-500' />,
    description: 'La categoría solicitada no existe',
    color: 'gray',
  };

  useEffect(() => {
    if (category && Object.keys(categoryInfo).includes(category)) {
      fetchProtocols();
    } else {
      setIsLoading(false);
      setError('Categoría no válida');
    }
  }, [category]);

  const fetchProtocols = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.getProtocols({
        category: category as ProtocolCategory,
        limit: 50,
        sortBy: 'title',
        sortOrder: 'asc',
      });

      setProtocols(response.protocols);
    } catch (err) {
      setError('Error al cargar los protocolos. Por favor, inténtalo de nuevo.');
      console.error('Error fetching protocols:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryIcon = (protocolCategory: string) => {
    switch (protocolCategory) {
      case 'medical':
        return <HeartIcon className='h-5 w-5 text-red-500' />;
      case 'surgical':
        return <ShieldCheckIcon className='h-5 w-5 text-blue-500' />;
      case 'cardiac':
        return <HeartIcon className='h-5 w-5 text-red-600' />;
      case 'emergency':
        return <ExclamationTriangleIcon className='h-5 w-5 text-yellow-500' />;
      case 'pediatric':
        return <UserGroupIcon className='h-5 w-5 text-green-500' />;
      case 'geriatric':
        return <ChartBarIcon className='h-5 w-5 text-purple-500' />;
      default:
        return <DocumentTextIcon className='h-5 w-5 text-gray-500' />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      active: { label: 'Activo', className: 'badge-success' },
      draft: { label: 'Borrador', className: 'badge-warning' },
      archived: { label: 'Archivado', className: 'badge-secondary' },
      deprecated: { label: 'Deprecado', className: 'badge-danger' },
    };

    const config = statusConfig[status] || { label: status, className: 'badge-secondary' };
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  if (isLoading) {
    return (
      <Layout showSidebar={true}>
        <div className='flex justify-center py-12'>
          <Loading size='lg' text='Cargando protocolos...' />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showSidebar={true}>
        <div className='text-center py-12'>
          <div className='text-red-600 mb-4'>{error}</div>
          <Link
            to='/protocols'
            className='inline-flex items-center px-4 py-2 bg-insurance-600 text-white rounded-lg hover:bg-insurance-700'
          >
            <ArrowLeftIcon className='h-4 w-4 mr-2' />
            Volver a Protocolos
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showSidebar={true}>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex items-center space-x-4 mb-6'>
          <Link
            to='/protocols'
            className='flex items-center text-gray-600 hover:text-gray-900 transition-colors'
          >
            <ArrowLeftIcon className='h-5 w-5 mr-2' />
            Volver a Protocolos
          </Link>
        </div>

        {/* Category Header */}
        <div
          className={`bg-gradient-to-r from-${currentCategory.color}-600 to-${currentCategory.color}-800 rounded-lg p-6 text-white`}
        >
          <div className='flex items-center space-x-4'>
            {currentCategory.icon}
            <div>
              <h1 className='text-2xl font-bold'>{currentCategory.label}</h1>
              <p className='text-gray-100 mt-1'>{currentCategory.description}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div className='card text-center'>
            <DocumentTextIcon className='h-8 w-8 text-insurance-600 mx-auto mb-2' />
            <div className='text-2xl font-bold text-gray-900'>{protocols.length}</div>
            <div className='text-sm text-gray-600'>Protocolos</div>
          </div>
          <div className='card text-center'>
            <HeartIcon className='h-8 w-8 text-insurance-600 mx-auto mb-2' />
            <div className='text-2xl font-bold text-gray-900'>
              {protocols.filter(p => p.status === 'active').length}
            </div>
            <div className='text-sm text-gray-600'>Activos</div>
          </div>
          <div className='card text-center'>
            <ChartBarIcon className='h-8 w-8 text-insurance-600 mx-auto mb-2' />
            <div className='text-2xl font-bold text-gray-900'>
              {protocols.reduce((sum, p) => sum + (p.viewCount || 0), 0)}
            </div>
            <div className='text-sm text-gray-600'>Total Vistas</div>
          </div>
        </div>

        {/* Protocols List */}
        {protocols.length === 0 ? (
          <div className='text-center py-12'>
            <DocumentTextIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No hay protocolos en esta categoría
            </h3>
            <p className='text-gray-600 mb-4'>
              Los protocolos aparecerán aquí cuando estén disponibles
            </p>
            <Link
              to='/protocols'
              className='inline-flex items-center px-4 py-2 bg-insurance-600 text-white rounded-lg hover:bg-insurance-700'
            >
              Ver Todos los Protocolos
            </Link>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {protocols.map(protocol => (
              <Link
                key={protocol._id}
                to={`/protocols/${protocol._id}`}
                className='protocol-card group'
              >
                <div className='flex items-start justify-between mb-3'>
                  <div className='flex items-center space-x-2'>
                    {getCategoryIcon(protocol.category)}
                    <span className='text-sm font-medium text-gray-600'>{protocol.category}</span>
                  </div>
                  {getStatusBadge(protocol.status)}
                </div>

                <h3 className='text-lg font-semibold text-gray-900 mb-2 group-hover:text-insurance-600 transition-colors'>
                  {protocol.title}
                </h3>

                <p className='text-gray-600 text-sm mb-4 line-clamp-2'>{protocol.description}</p>

                <div className='flex items-center justify-between text-sm text-gray-500'>
                  <span>v{protocol.version}</span>
                  <span>{protocol.viewCount || 0} vistas</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CategoryPage;
